<script setup>
import { Head, Link, useForm } from '@inertiajs/vue3';
import AuthenticationCard from '@/Components/AuthenticationCard.vue';
import AuthenticationCardLogo from '@/Components/AuthenticationCardLogo.vue';
import Checkbox from '@/Components/Checkbox.vue';
import InputError from '@/Components/InputError.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import SecondaryButton from '@/Components/SecondaryButton.vue';
import TextInput from '@/Components/TextInput.vue';
import DialogModal from '@/Components/DialogModal.vue';
import { ref, watch } from 'vue'; // Importa ref e watch da Vue

defineProps({
    canResetPassword: Boolean,
    status: String
});

defineOptions({
    layout: null,
});

// Usa ref per creare una variabile reattiva
const showPassword = ref(false);
const showSessionModal = ref(false);

const form = useForm({
    email: '',
    password: '',
    remember: false,
    force_logout: false,
});

// Watch for the already_logged_in error to show the modal
watch(() => form.errors.already_logged_in, (error) => {
    if (error) {
        showSessionModal.value = true;
    }
});

const submit = () => {
    form.transform(data => ({
        ...data,
        remember: form.remember ? 'on' : '',
    })).post(route('login'), {
        onFinish: () => {
            if (!form.errors.already_logged_in) {
                form.reset('password');
            }
        },
    });
};

const closeSessionModal = () => {
    showSessionModal.value = false;
    form.clearErrors('already_logged_in');
    form.force_logout = false;
};

const forceLogoutAndLogin = () => {
    showSessionModal.value = false;
    form.force_logout = true;
    submit();
};
</script>

<template>
    <Head :title="$t('auth.log_in_title')" />

    <AuthenticationCard>
        <template #logo>
            <AuthenticationCardLogo />
        </template>

        <div v-if="status" class="mb-4 font-medium text-sm text-green-600">
            {{ status }}
        </div>

        <div v-if="form.errors.too_many_customer_sessions" class="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <div class="flex">
                <div class="flex-shrink-0">
                    <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                    </svg>
                </div>
                <div class="ml-3">
                    <p class="text-sm font-medium text-red-800">{{ form.errors.too_many_customer_sessions }}</p>
                </div>
            </div>
        </div>

        <form @submit.prevent="submit" autocomplete="off">
            <div>
                <TextInput
                    :label="$t('auth.email')"
                    :placeholder="$t('auth.email')"
                    id="email"
                    v-model="form.email"
                    type="email"
                    class="p93__input"
                    required
                    autofocus
                    aria-autocomplete="none"
                />
                <InputError class="mt-2" :message="form.errors.email" />
            </div>

            <div class="mt-4">
                <div class="relative">
                    <TextInput
                        :label="$t('auth.password_label')"
                        :placeholder="$t('auth.password_label')"
                        id="password"
                        v-model="form.password"
                        :type="showPassword ? 'text' : 'password'"
                        class="p93__input"
                        required
                        autocomplete="current-password"
                    />
                    <button 
                        type="button" 
                        class="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                        @touchstart="showPassword = true"
                        @touchend="showPassword = false"
                        @touchcancel="showPassword = false"
                        @mousedown="showPassword = true"
                        @mouseup="showPassword = false"
                        @mouseleave="showPassword = false"
                    >
                        <svg 
                            class="h-5 w-5 text-gray-500" 
                            :class="{'text-gray-700': showPassword}"
                            fill="none" 
                            xmlns="http://www.w3.org/2000/svg" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                        >
                            <path 
                                v-if="showPassword.value" 
                                stroke-linecap="round" 
                                stroke-linejoin="round" 
                                stroke-width="2" 
                                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7A9.97 9.97 0 014.02 8.971m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88L6.59 6.59m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" 
                            />
                            <path 
                                v-else 
                                stroke-linecap="round" 
                                stroke-linejoin="round" 
                                stroke-width="2" 
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" 
                            />
                            <path 
                                v-if="!showPassword.value" 
                                stroke-linecap="round" 
                                stroke-linejoin="round" 
                                stroke-width="2" 
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" 
                            />
                        </svg>
                    </button>
                </div>
                <InputError class="mt-2" :message="form.errors.password" />
            </div>

            <div class="block mt-4">
                <label class="flex items-center">
                    <Checkbox v-model:checked="form.remember" name="remember" />
                    <span class="ms-2 text-sm text-gray-600">{{ $t('auth.remember_me') }}</span>
                </label>
            </div>

            <div class="flex items-center justify-end mt-4">
                <Link v-if="canResetPassword" :href="route('password.request')" class="underline text-sm text-gray-600 hover:text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                    {{ $t('auth.forgot_password') }}
                </Link>

                <PrimaryButton class="ms-4" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                    {{ $t('auth.log_in_title') }}
                </PrimaryButton>
            </div>
        </form>

        <!-- Session Already Active Modal -->
        <DialogModal :show="showSessionModal" @close="closeSessionModal">
            <template #title>
                <div class="flex items-center">
                    <svg class="h-6 w-6 text-yellow-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {{ $t('auth.session_already_active') }}
                </div>
            </template>

            <template #content>
                <div class="text-gray-600">
                    <p class="font-medium text-gray-900 mb-2">{{ form.errors.already_logged_in }}</p>
                    <p>{{ $t('auth.force_logout_prompt') }}</p>
                </div>
            </template>

            <template #footer>
                <SecondaryButton @click="closeSessionModal" :disabled="form.processing">
                    {{ $t('actions.cancel') }}
                </SecondaryButton>

                <PrimaryButton 
                    class="ms-3" 
                    @click="forceLogoutAndLogin"
                    :class="{ 'opacity-25': form.processing }" 
                    :disabled="form.processing"
                >
                    {{ $t('auth.force_logout_button') }}
                </PrimaryButton>
            </template>
        </DialogModal>
    </AuthenticationCard>
</template>
